// hooks/bootState.ts
// Pure boot readiness — NO imports from React or Firebase. In the shield
// architecture this is consumed only as the "is boot still in progress?"
// oracle: BootShield keeps the splash up while deriveBootState() === 'booting'.
// Table-tested in tests/bootState.test.ts against the 12 characterization scenarios.
import type { UserRole } from '../types';

export type BootState =
  | 'booting' | 'login' | 'unprovisioned' | 'join'
  | 'participant' | 'superadmin' | 'researcher' | 'chooser';

export interface BootInputs {
  /** first onAuthStateChanged callback has fired */
  authResolved: boolean;
  hasUser: boolean;
  /** a token read (cached or fresh) has completed for this user */
  claimsKnown: boolean;
  claimsRole: UserRole | null;
  /** first users/{uid} snapshot arrived, or its 5s timeout fired */
  profileSettled: boolean;
  /** first onDataChange emission arrived, or its 10s timeout fired */
  dataReady: boolean;
  /** saved project id exists in loaded data (false when none saved or deleted) */
  savedProjectValid: boolean;
  /** pendingProjectJoin present in localStorage */
  pendingJoin: boolean;
}

export function deriveBootState(i: BootInputs): BootState {
  if (!i.authResolved) return 'booting';
  if (!i.hasUser) return 'login';
  if (!i.claimsKnown || !i.profileSettled) return 'booting';
  if (i.pendingJoin) return 'join';
  if (i.claimsRole === null) return i.dataReady ? 'unprovisioned' : 'booting';
  // Participants need appData too (project, tasks) — without this the shield
  // lifted on claims+profile alone and faded into a second loading shell.
  if (i.claimsRole === 'participant') return i.dataReady ? 'participant' : 'booting';
  if (i.claimsRole === 'superadmin') return 'superadmin';
  // researcher | orgadmin
  if (!i.dataReady) return 'booting';
  return i.savedProjectValid ? 'researcher' : 'chooser';
}
